import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowUpRightFromSquare,
  faChevronLeft,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavLink } from '~/components/NavLink';
import { Navbar } from '~/components/nav/Navbar';
import styles from '~/layouts/nav/Nav.module.css';
import { SettingsLink } from '~/layouts/nav/SettingsLink';

export type NavSettingsMenuItem = {
  key: string;
  label: string;
  to: string;
  activeKey?: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  showPopupIcon?: boolean;
};

type NavSettingsPanelProps = {
  isOpen: boolean;
  onToggle: () => void;
  menuItems: NavSettingsMenuItem[];
  hasItems: boolean;
  /** Optional callback to handle custom navigation when closing config */
  onCloseConfig?: () => void;
};

// Pure function to determine default route based on current path
function getDefaultRoute(currentPath: string): string {
  if (
    currentPath.startsWith('/job-templates') ||
    currentPath.startsWith('/automations')
  ) {
    return '/jobs/scheduled';
  }
  if (currentPath.startsWith('/spaces') || currentPath.startsWith('/site-')) {
    return '/sites';
  }
  if (
    currentPath.startsWith('/asset-config') ||
    currentPath.startsWith('/asset-models')
  ) {
    return '/assets';
  }
  if (currentPath.startsWith('/config') || currentPath.startsWith('/items')) {
    return '/stock-on-hand';
  }
  return currentPath;
}

export const NavSettingsPanel = memo<NavSettingsPanelProps>(
  function NavSettingsPanel({
    isOpen,
    onToggle,
    menuItems,
    hasItems,
    onCloseConfig,
  }: NavSettingsPanelProps) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    // Default routes mapping moved outside component for stability

    const handleCloseAndNavigate = useCallback(() => {
      const currentPath = location.pathname;
      const defaultRoute = getDefaultRoute(currentPath);

      // Only navigate if we're on a config route and have a different default route
      if (defaultRoute !== currentPath) {
        if (onCloseConfig) {
          onCloseConfig();
        } else {
          navigate(defaultRoute);
        }
      }

      // Always call the original onToggle to close the panel
      onToggle();
    }, [location.pathname, onCloseConfig, navigate, onToggle]);

    if (!hasItems) return null;

    return (
      <div
        className={classNames(
          'flex items-center',
          isOpen
            ? 'ease-standard relative -ml-8 rounded-r-full rounded-br-full rounded-tr-full !bg-[#FCFBFF] pl-8 pr-4 transition-[background-color,border-radius] duration-500 after:pointer-events-none after:absolute after:-inset-0.5 after:rounded-r-full after:border after:border-l-0 after:border-outline-bright'
            : 'ease-standard mr-5 transition-[background-color,border-radius] duration-200'
        )}
        style={{
          transition: isOpen
            ? 'padding-left 0.2s ease-standard, background-color 0.2s ease-standard'
            : 'padding-left 0.3s ease-standard, background-color 0.2s ease-standard',
        }}
        data-usetiful='settings-panel'
      >
        {/* Settings menu items with CSS transitions (expand left -> right) */}
        <div
          className={classNames(
            styles.subnav,
            'flex origin-left overflow-hidden',
            isOpen ? 'mr-1 max-w-full opacity-100' : 'max-w-0 opacity-0'
          )}
          style={{
            clipPath: isOpen ? 'inset(0 0 0 0)' : 'inset(0 100% 0 0)',
            transition: isOpen
              ? 'max-width 0.35s ease-standard 0.05s, opacity 0.25s ease-standard 0.05s, margin-right 0.35s ease-standard 0.05s, clip-path 0.35s ease-standard 0.05s'
              : 'max-width 0.3s ease-standard, opacity 0.2s ease-standard, margin-right 0.3s ease-standard, clip-path 0.3s ease-standard',
          }}
        >
          {menuItems.map((item, index) => {
            const isHashTarget =
              typeof item.to === 'string' &&
              (item.to.startsWith('#org-settings-open') ||
                item.to.startsWith('#user-settings-open'));
            return (
              <Navbar.Item
                key={item.key}
                as={NavLink}
                to={item.to}
                activeKey={item.activeKey || item.key}
                isActive={() => location.pathname.startsWith(item.to)}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  if (isHashTarget) {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate({ hash: item.to }, { replace: false });
                    return;
                  }
                  if (item.onClick) item.onClick(e);
                }}
                data-usetiful={`settings-panel-link-${item.key}`}
                className={classNames(
                  isOpen
                    ? 'translate-x-0 transform opacity-100'
                    : '-translate-x-4 transform opacity-0',
                  location.pathname.startsWith(item.to) && '!text-brand'
                )}
                style={{
                  transition: isOpen
                    ? `transform 0.25s ease-standard ${
                        0.2 + index * 0.05
                      }s, opacity 0.25s ease-standard ${0.2 + index * 0.05}s`
                    : 'transform 0.1s ease-standard, opacity 0.1s ease-standard',
                }}
              >
                {item.showPopupIcon ? (
                  <span className='flex items-center gap-1.5'>
                    {item.label}
                    <FontAwesomeIcon
                      icon={faArrowUpRightFromSquare as IconProp}
                      className='text-xs opacity-60'
                      aria-hidden='true'
                    />
                  </span>
                ) : (
                  item.label
                )}
              </Navbar.Item>
            );
          })}
        </div>
        {/* Toggle button on the far right of the panel */}
        <div
          data-usetiful={`settings-panel-button-${isOpen ? 'open' : 'closed'}`}
        >
          {isOpen ? (
            <button
              onClick={handleCloseAndNavigate}
              aria-label={t('close')}
              className='flex h-9 w-9 items-center justify-center rounded-full border border-outline-variant bg-white hover:bg-surface-container-bright focus:bg-surface-container-high'
            >
              <FontAwesomeIcon icon={faChevronLeft as IconProp} />
            </button>
          ) : (
            <SettingsLink onClick={onToggle} iconButton={true} />
          )}
        </div>
      </div>
    );
  }
);

NavSettingsPanel.displayName = 'NavSettingsPanel';
